import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";

// Get API URL from environment
const getApiUrl = () => {
  const apiUrl =
    (window as any)?.ENV?.REACT_APP_API_URL ||
    process.env.REACT_APP_API_URL ||
    "/api";
  console.log("🔧 API Service - Base URL configured as:", apiUrl);
  return apiUrl;
};

// Create axios instance
const api: AxiosInstance = axios.create({
  baseURL: getApiUrl(),
  timeout: 30000, // Increased from 10s to 30s for dashboard operations
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // // Debug logging
    // console.log(
    //   `🚀 API Request: ${config.method?.toUpperCase()} ${config.baseURL}${
    //     config.url
    //   }`
    // );
    // console.log(
    //   `🔑 Auth Header: ${config.headers.Authorization ? "Present" : "None"}`
    // );

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Response interceptor to handle common errors
api.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Only logout for authentication-related 401s, not resource access 401s
      const errorMessage = error.response?.data?.message || "";
      const isAuthError =
        errorMessage.includes("token") ||
        errorMessage.includes("authentication") ||
        errorMessage.includes("Invalid token") ||
        errorMessage.includes("Access denied");

      console.log("401 Error Details:", {
        url: error.config?.url,
        message: errorMessage,
        isAuthError,
        data: error.response?.data,
      });

      // Only logout if it's actually an authentication error
      if (isAuthError || !errorMessage) {
        console.log("Authentication error detected, logging out...");
        localStorage.removeItem("token");
        delete api.defaults.headers.common["Authorization"];
        // Force page reload to clear auth state
        window.location.href = "/login";
      } else {
        console.log("Resource access 401, not logging out");
      }
    }
    return Promise.reject(error);
  },
);

// API methods
export const apiService = {
  // Generic methods
  get: <T = any>(
    url: string,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>> => {
    return api.get<T>(url, config);
  },

  post: <T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>> => {
    return api.post<T>(url, data, config);
  },

  put: <T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>> => {
    return api.put<T>(url, data, config);
  },

  delete: <T = any>(
    url: string,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>> => {
    return api.delete<T>(url, config);
  },

  patch: <T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>> => {
    return api.patch<T>(url, data, config);
  },

  // Set auth token (for AuthContext)
  setAuthToken: (token: string | null) => {
    if (token) {
      api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    } else {
      delete api.defaults.headers.common["Authorization"];
    }
  },

  // Upload image for chat - always use production URL with retry logic
  uploadChatImage: async (
    file: File,
    retryCount = 0,
  ): Promise<AxiosResponse<any>> => {
    const formData = new FormData();
    formData.append("image", file);

    // Always use production URL for image uploads
    const uploadUrl = (window as any)?.ENV?.REACT_APP_UPLOAD_URL || "/api";
    console.log("📤 Uploading image to production server:", uploadUrl);

    try {
      return await axios.post(`${uploadUrl}/upload/chat-image`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
    } catch (error: any) {
      // Handle 429 rate limit error with retry
      if (error.response?.status === 429 && retryCount < 3) {
        const retryAfter = error.response?.headers?.["retry-after"] || 5;
        const delay = parseInt(retryAfter) * 1000 * (retryCount + 1); // Exponential backoff

        console.log(
          `⏳ Rate limited. Retrying in ${delay}ms (attempt ${
            retryCount + 1
          }/3)`,
        );

        await new Promise((resolve) => setTimeout(resolve, delay));
        return apiService.uploadChatImage(file, retryCount + 1);
      }

      // Re-throw other errors
      throw error;
    }
  },

  // Upload general file (documents, audio, video, etc.)
  uploadGeneralFile: async (
    file: File,
    retryCount = 0,
  ): Promise<AxiosResponse<any>> => {
    const formData = new FormData();
    formData.append("file", file); // Different field name

    // Always use production URL for general file uploads
    const uploadUrl = (window as any)?.ENV?.REACT_APP_UPLOAD_URL || "/api";
    console.log("📤 Uploading file to production server:", uploadUrl);

    try {
      return await axios.post(`${uploadUrl}/upload/general`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
    } catch (error: any) {
      // Handle 429 rate limit error with retry
      if (error.response?.status === 429 && retryCount < 3) {
        const retryAfter = error.response?.headers?.["retry-after"] || 5;
        const delay = parseInt(retryAfter) * 1000 * (retryCount + 1); // Exponential backoff

        console.log(
          `⏳ Rate limited. Retrying in ${delay}ms (attempt ${
            retryCount + 1
          }/3)`,
        );

        await new Promise((resolve) => setTimeout(resolve, delay));
        return apiService.uploadGeneralFile(file, retryCount + 1);
      }

      // Re-throw other errors
      throw error;
    }
  },

  // WhatsApp Media Gallery methods
  uploadWhatsAppMedia: async (
    file: File,
    caption?: string,
    tags?: string[],
    onUploadProgress?: (percent: number) => void,
  ): Promise<AxiosResponse<any>> => {
    const formData = new FormData();
    formData.append("media", file);
    if (caption) formData.append("caption", caption);
    if (tags) formData.append("tags", JSON.stringify(tags));

    return api.post("/whatsapp-media/upload", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      timeout: 300000, // 5 minutes — includes server relay to WhatsApp
      onUploadProgress: (event) => {
        if (onUploadProgress && event.total) {
          onUploadProgress(Math.round((event.loaded * 100) / event.total));
        }
      },
    });
  },

  getWhatsAppMediaGallery: async (params?: {
    page?: number;
    limit?: number;
    sortBy?: string;
    sortOrder?: "asc" | "desc";
    tags?: string[];
    mimeType?: string;
    search?: string;
  }): Promise<AxiosResponse<any>> => {
    return api.get("/whatsapp-media/gallery", { params });
  },

  getWhatsAppMediaDetails: async (
    mediaId: string,
  ): Promise<AxiosResponse<any>> => {
    return api.get(`/whatsapp-media/${mediaId}`);
  },

  getWhatsAppMediaUrl: async (mediaId: string): Promise<AxiosResponse<any>> => {
    return api.get(`/whatsapp-media/${mediaId}/url`);
  },

  sendWhatsAppMedia: async (
    mediaId: string,
    recipientNumber: string,
    caption?: string,
    whatsAppNumberId?: string,
  ): Promise<AxiosResponse<any>> => {
    return api.post(`/whatsapp-media/${mediaId}/send`, {
      recipientNumber,
      caption,
      whatsAppNumberId,
    });
  },

  updateWhatsAppMedia: async (
    mediaId: string,
    data: { caption?: string; tags?: string[] },
  ): Promise<AxiosResponse<any>> => {
    return api.put(`/whatsapp-media/${mediaId}`, data);
  },

  deleteWhatsAppMedia: async (mediaId: string): Promise<AxiosResponse<any>> => {
    return api.delete(`/whatsapp-media/${mediaId}`);
  },

  // Get base URL
  getBaseURL: () => api.defaults.baseURL,

  // Conversation status methods
  getConversationStatus: async (
    whatsAppNumberId: string,
    contactId: string,
  ): Promise<AxiosResponse<any>> => {
    const isValidObjectId = (id: string) =>
      !!id && id !== "undefined" && /^[a-f\d]{24}$/i.test(id);

    if (!isValidObjectId(whatsAppNumberId) || !isValidObjectId(contactId)) {
      return Promise.reject(new Error("Invalid WhatsApp number or contact ID"));
    }

    return api.get(`/messages/number/${whatsAppNumberId}/status/${contactId}`);
  },

  // WhatsApp template methods
  getWhatsAppTemplates: async (): Promise<AxiosResponse<any>> => {
    return api.get("/whatsapp-templates");
  },

  sendWhatsAppTemplate: async (
    to: string,
    templateName: string,
    templateLanguage: string,
    components: any[],
    whatsappNumberId: string,
  ): Promise<AxiosResponse<any>> => {
    return api.post("/whatsapp-templates/send", {
      to,
      templateName,
      templateLanguage,
      components,
      whatsappNumberId,
    });
  },

  getTemplateSendHistory: async (params?: {
    page?: number;
    limit?: number;
    search?: string;
    templateName?: string;
    status?: string;
    createdBy?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<AxiosResponse<any>> => {
    return api.get("/whatsapp-templates/history", { params });
  },

  getActiveTemplateSendSessions: async (): Promise<AxiosResponse<any>> => {
    return api.get("/whatsapp-templates/active");
  },

  getTemplateSendSessionDetails: async (
    sessionId: string,
    params?: { page?: number; limit?: number },
  ): Promise<AxiosResponse<any>> => {
    return api.get(`/whatsapp-templates/sessions/${sessionId}`, { params });
  },

  getTemplateButtonActions: async (
    templateName?: string,
  ): Promise<AxiosResponse<any>> => {
    return api.get("/whatsapp-templates/button-actions", {
      params: templateName ? { templateName } : undefined,
    });
  },

  saveTemplateButtonActions: async (data: {
    templateName: string;
    templateLanguage?: string;
    actions: Array<{
      buttonText: string;
      actionType: string;
      actionPayload?: Record<string, unknown>;
      isActive?: boolean;
    }>;
  }): Promise<AxiosResponse<any>> => {
    return api.put("/whatsapp-templates/button-actions", data);
  },

  deleteTemplateButtonAction: async (
    actionId: string,
  ): Promise<AxiosResponse<any>> => {
    return api.delete(`/whatsapp-templates/button-actions/${actionId}`);
  },

  // Personal template methods
  getPersonalTemplates: async (params?: {
    page?: number;
    limit?: number;
    search?: string;
    category?: string;
  }): Promise<AxiosResponse<any>> => {
    return api.get("/personal-templates", { params });
  },

  createPersonalTemplate: async (data: {
    name: string;
    content: string;
    variables?: string[] | Array<{ name: string }>;
    category?: string;
  }): Promise<AxiosResponse<any>> => {
    return api.post("/personal-templates", data);
  },

  updatePersonalTemplate: async (
    id: string,
    data: Record<string, unknown>,
  ): Promise<AxiosResponse<any>> => {
    return api.put(`/personal-templates/${id}`, data);
  },

  deletePersonalTemplate: async (id: string): Promise<AxiosResponse<any>> => {
    return api.delete(`/personal-templates/${id}`);
  },

  sendPersonalTemplate: async (data: {
    to: string;
    personalTemplateId: string;
    variableValues?: Record<string, string>;
    whatsAppNumberId: string;
  }): Promise<AxiosResponse<any>> => {
    return api.post("/personal-templates/send", data);
  },

  startPersonalTemplateBulk: async (data: {
    personalTemplateId: string;
    whatsAppNumberId: string;
    whatsAppNumberIds?: string[];
    recipients: Array<{
      recipientNumber: string;
      variableValues?: Record<string, string>;
      messagePreview?: string;
    }>;
    pacingPreset?: string;
    pacing?: Record<string, number>;
    dailyCapPerNumber?: number;
    quietHours?: { start: string; end: string; timezone: string };
    warmup?: boolean;
    priorInboundOnly?: boolean;
  }): Promise<AxiosResponse<any>> => {
    return api.post("/personal-templates/start", data);
  },

  pausePersonalTemplateSend: async (
    sessionId: string,
  ): Promise<AxiosResponse<any>> => {
    return api.post(`/personal-templates/pause/${sessionId}`);
  },

  resumePersonalTemplateSend: async (
    sessionId: string,
  ): Promise<AxiosResponse<any>> => {
    return api.post(`/personal-templates/resume/${sessionId}`);
  },

  cancelPersonalTemplateSend: async (
    sessionId: string,
  ): Promise<AxiosResponse<any>> => {
    return api.post(`/personal-templates/cancel/${sessionId}`);
  },

  cancelWhatsAppTemplateSend: async (
    sessionId: string,
  ): Promise<AxiosResponse<any>> => {
    return api.post(`/whatsapp-templates/cancel/${sessionId}`);
  },

  previewPersonalTemplateVariants: async (data: {
    personalTemplateId?: string;
    content?: string;
    variableValues?: Record<string, string>;
    count?: number;
  }): Promise<AxiosResponse<any>> => {
    return api.post("/personal-templates/preview-variants", data);
  },

  estimatePersonalTemplateCampaign: async (params: {
    recipientCount: number;
    whatsAppNumberId: string;
    dailyCapPerNumber?: number;
    warmup?: boolean;
    quietHours?: { start: string; end: string; timezone: string };
  }): Promise<AxiosResponse<any>> => {
    return api.get("/personal-templates/estimate", {
      params: {
        ...params,
        quietHours: params.quietHours
          ? JSON.stringify(params.quietHours)
          : undefined,
      },
    });
  },

  getPersonalTemplateHistory: async (params?: {
    page?: number;
    limit?: number;
    search?: string;
    templateName?: string;
    status?: string;
    createdBy?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<AxiosResponse<any>> => {
    return api.get("/personal-templates/history", { params });
  },

  getActivePersonalTemplateSessions: async (): Promise<AxiosResponse<any>> => {
    return api.get("/personal-templates/active");
  },

  getPersonalTemplateSessionDetails: async (
    sessionId: string,
    params?: { page?: number; limit?: number },
  ): Promise<AxiosResponse<any>> => {
    return api.get(`/personal-templates/sessions/${sessionId}`, { params });
  },
};

export default api;
